import type { Game } from './catalog'

/** The half of the launch contract this app owns: who plays, with what. */
export interface Session {
  /** uuid — see `player.ts`; this is the account, not a display name. */
  player: string
  /** Free text while it is being typed, so it is normalised on the way out. */
  balance: string
  currency: string
  language: string
}

/**
 * Percent-encoding that leaves `:` and `/` alone.
 *
 * Both are legal in a query component (RFC 3986 §3.4), and two of the four
 * values carrying them — the RGS url and the `uuid:balance:currency` key — are
 * the two a tester reads off the preview box and pastes into a terminal.
 * Escaping them costs readability and buys nothing.
 */
function encode(value: string): string {
  return encodeURIComponent(value).replace(/%3A/g, ':').replace(/%2F/g, '/')
}

/** Digits only: the key is split positionally, so a separator would shift it. */
export function balanceAmount(raw: string): number {
  return Number(String(raw).replace(/[^0-9.]/g, '')) || 0
}

/**
 * `<uuid>:<balance>:<currency>`, the shape `readLaunchParams` splits.
 *
 * Currency goes down lowercased to match the RGS's own default (`usd`); the
 * selector shows it uppercase because that is how a tester reads a currency.
 */
export function launchKey(session: Session): string {
  return `${session.player}:${balanceAmount(session.balance)}:${session.currency.toLowerCase()}`
}

/**
 * The URL that opens the game.
 *
 * Handing over an explicit `key` is what makes this a launcher rather than a
 * bookmark: the game only mints its own player, balance and currency when no
 * key arrives, and then the session is the browser's rather than ours.
 *
 * A prototype gets none of that. There is no RGS behind it to hold a wallet,
 * so a key would name an account that cannot exist — the build is opened at its
 * bare URL and makes its own arrangements.
 */
export function launchUrl(game: Game, session: Session): string {
  const base = `${game.baseUrl.replace(/\/+$/, '')}/${game.slug}`
  if (game.type === 'prototype') return base

  const params: ReadonlyArray<readonly [string, string]> = [
    ['server', game.launch.server],
    ['game', game.launch.game],
    ['provider', game.launch.provider],
    ['wallet', game.launch.wallet],
    ['operator', game.launch.operator],
    ['language', session.language],
    ['key', launchKey(session)],
  ]

  const query = params.map(([name, value]) => `${name}=${encode(value)}`).join('&')

  // The slug is the path segment the game is deployed under, not a parameter —
  // `…/silverfoxgames/dummy-slot`. Distinct from `launch.game`, which is the
  // same game's id on the RGS.
  return `${base}?${query}`
}
